import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import { usePageMeta } from '@/hooks/usePageMeta';

const features = [
  {
    icon: 'ri-shuffle-line',
    title: '랜덤 번호 생성',
    description: '한 번의 클릭으로 6개의 번호를 무작위로 생성합니다. 여러 게임을 동시에 생성할 수 있어 편리해요.',
    color: 'bg-accent-100 text-accent-700',
    steps: [
      '홈 화면에서 "무작위 생성" 섹션으로 이동하세요.',
      '원하는 게임 수(1~5게임)를 선택하세요.',
      '"번호 생성하기" 버튼을 누르면 즉시 번호가 생성됩니다.',
      '생성된 번호는 자동으로 정렬되어 보기 좋게 표시됩니다.',
    ],
  },
  {
    icon: 'ri-star-line',
    title: '럭키 번호 생성',
    description: '이름, 생년월일, 좋아하는 숫자 등 개인 정보를 입력하면 나만의 행운 번호를 추천해드려요.',
    color: 'bg-primary-100 text-primary-700',
    steps: [
      '홈 화면에서 "럭키 생성" 섹션으로 이동하세요.',
      '이름이나 닉네임, 특별한 날짜를 입력하세요.',
      '좋아하는 숫자나 색상을 선택할 수 있어요.',
      '"내 행운 번호 받기" 버튼을 누르면 맞춤 번호가 생성됩니다.',
    ],
  },
  {
    icon: 'ri-brain-line',
    title: '스마트 추천',
    description: '역대 당첨 데이터를 분석하여 빈도수, 미출현 기간, 패턴을 기반으로 최적의 번호 조합을 추천합니다.',
    color: 'bg-secondary-100 text-secondary-700',
    steps: [
      '홈 화면에서 "스마트 추천" 섹션으로 이동하세요.',
      '추천 전략을 선택할 수 있습니다 (고빈도, 저빈도, 밸런스).',
      '분석 결과를 바탕으로 추천 번호 조합이 표시됩니다.',
      '마음에 드는 번호는 저장 버튼으로 보관할 수 있어요.',
    ],
  },
  {
    icon: 'ri-bar-chart-line',
    title: '통계 분석 (회원 전용)',
    description: '역대 당첨 번호의 빈도수, 핫/콜드 번호, 번호 쌍 분석, 홀짝 비율까지 다양한 각도로 분석합니다.',
    color: 'bg-secondary-100 text-secondary-700',
    steps: [
      '회원가입 후 통계 페이지에 접속하세요.',
      '번호별 출현 빈도를 막대 그래프로 한눈에 확인할 수 있어요.',
      '핫 넘버(최근 자주 등장한 번호)와 콜드 넘버(오랫동안 미출현한 번호)를 비교하세요.',
      '번호 쌍(Pair)과 트리플(Triple) 분석으로 자주 함께 등장하는 번호 조합을 발견할 수 있습니다.',
    ],
  },
];

const faqs = [
  {
    q: 'LottoLab은 실제 로또 당첨을 보장하나요?',
    a: '아니요. LottoLab은 로또 번호 추천 및 통계 분석 도구일 뿐, 당첨을 보장하지 않습니다. 모든 번호는 참고용이며, 실제 구매는 사용자의 판단에 맡깁니다. 로또는 완전한 확률 게임이에요.',
  },
  {
    q: '회원가입 없이도 사용할 수 있나요?',
    a: '네! 랜덤 생성과 럭키 생성, 기본 추천 기능은 누구나 무료로 이용할 수 있습니다. 다만 통계 분석과 번호 저장 기능은 회원 전용이에요. 회원가입은 1분도 걸리지 않아요.',
  },
  {
    q: '생성된 번호는 어디에 저장되나요?',
    a: '회원가입 후 "내 번호" 페이지에서 생성한 번호를 저장하고 관리할 수 있습니다. 저장된 번호는 언제든지 다시 확인할 수 있고, 실제 당첨 결과와 비교도 가능해요.',
  },
  {
    q: '통계 데이터는 얼마나 자주 업데이트되나요?',
    a: '매주 토요일 동행복권 추첨 이후 자동으로 업데이트됩니다. 최신 회차 데이터를 반영하여 항상 정확한 통계를 제공해요.',
  },
  {
    q: '모바일에서도 사용할 수 있나요?',
    a: '물론이에요! LottoLab은 모바일과 데스크톱 모두에서 최적화된 화면을 제공합니다. 언제 어디서든 편리하게 번호를 생성하고 확인하세요.',
  },
];

export default function Guide() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  usePageMeta({
    title: 'LottoLab 이용방법 & 서비스 소개 | 로또 번호 생성·통계·추천 가이드',
    description: 'LottoLab 서비스 이용방법을 상세히 안내합니다. 랜덤 번호 생성, 럭키 번호 추천, 스마트 추천, 통계 분석 등 모든 기능의 단계별 사용법과 FAQ를 확인하세요.',
    keywords: '로또 이용방법, 로또번호 생성방법, 로또 통계 분석, 로또 번호 추천, LottoLab 가이드, 로또랩 사용법',
    canonical: 'https://lottolab.app/guide',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    },
  });

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-16">
        <div className="w-full px-4 md:px-6 py-16 md:py-24 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-6">
              <i className="ri-book-open-line"></i>
              <span>서비스 소개 &amp; 이용 가이드</span>
            </div>

            <h1 className="font-heading text-4xl md:text-5xl text-foreground-900 mb-4 leading-tight font-bold">
              LottoLab,
              <br className="sm:hidden" />
              <span className="text-secondary-500"> 처음이신가요?</span>
            </h1>

            <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              LottoLab은 데이터 기반 로또 번호 분석 연구소예요.
              <br className="hidden sm:block" />
              랜덤 생성부터 통계 분석까지, 모든 기능을 쉽고 빠르게 이용하는 방법을 알려드릴게요.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3 font-bold">
              주요 기능 살펴보기
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              번호 생성부터 통계 분석까지, LottoLab의 모든 기능을 소개합니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200"
              >
                <div className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-5`}>
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-900 font-bold mb-3">
                  {feature.title}
                </h3>
                <p className="text-foreground-500 text-sm leading-relaxed mb-5">
                  {feature.description}
                </p>
                <div className="space-y-2.5">
                  {feature.steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary-500 text-white text-xs font-bold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span className="text-foreground-600 text-sm leading-relaxed">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Start */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3 font-bold">
              LottoLab 시작하기
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              단 3단계로 LottoLab의 모든 기능을 활용해보세요
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary-500 text-white flex items-center justify-center mx-auto mb-5">
                <i className="ri-user-add-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900 font-bold mb-2">회원가입</h3>
              <p className="text-foreground-500 text-sm leading-relaxed">
                이메일과 비밀번호만 입력하면 1분 만에 가입 완료! 간편하게 시작하세요.
              </p>
              <button
                onClick={() => navigate('/register')}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                회원가입 하러가기
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>

            {/* Step 2 */}
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-2xl bg-secondary-500 text-white flex items-center justify-center mx-auto mb-5">
                <i className="ri-shuffle-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900 font-bold mb-2">번호 생성</h3>
              <p className="text-foreground-500 text-sm leading-relaxed">
                랜덤, 럭키, 스마트 추천 중 원하는 방식으로 나만의 번호를 만들어보세요.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-secondary-600 hover:text-secondary-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                번호 생성하러 가기
                <i className="ri-arrow-right-line text-sm"></i>
              </button>
            </div>

            {/* Step 3 */}
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-2xl bg-accent-500 text-white flex items-center justify-center mx-auto mb-5">
                <i className="ri-heart-line text-2xl"></i>
              </div>
              <h3 className="font-heading text-lg text-foreground-900 font-bold mb-2">번호 저장 &amp; 분석</h3>
              <p className="text-foreground-500 text-sm leading-relaxed">
                마음에 드는 번호는 저장하고, 통계 데이터로 더 똑똑하게 다음 번호를 준비하세요.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3 font-bold">
              자주 묻는 질문
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
              LottoLab에 대해 궁금한 점들을 모아봤어요
            </p>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-background-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-background-50 transition-colors"
                >
                  <span className="font-medium text-foreground-800 text-sm md:text-base pr-4">
                    {faq.q}
                  </span>
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${openFaq === index ? 'bg-secondary-500 text-white rotate-180' : 'bg-background-200 text-foreground-500'}`}>
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </div>
                </button>
                <div
                  className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${openFaq === index ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}
                  `}
                >
                  <p className="px-6 pb-5 text-foreground-500 text-sm leading-relaxed border-t border-background-100 pt-4">
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-3xl mx-auto bg-primary-800 rounded-3xl p-10 md:p-16 text-center text-white">
          <h2 className="font-heading text-2xl md:text-3xl mb-4 font-bold">
            이제 시작해볼까요?
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-8 max-w-lg mx-auto">
            회원가입하고 나만의 행운 번호를 만들어보세요.
            <br />
            모든 기본 기능은 무료로 제공됩니다.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('/register')}
              className="
                inline-flex items-center gap-2 px-8 py-3.5 rounded-full
                bg-secondary-500 text-white font-bold text-base
                hover:bg-secondary-600 transition-all duration-200
                whitespace-nowrap cursor-pointer
              "
            >
              <i className="ri-user-add-line"></i>
              무료로 시작하기
            </button>
            <button
              onClick={() => navigate('/')}
              className="
                inline-flex items-center gap-2 px-8 py-3.5 rounded-full
                border-2 border-white/30 text-white font-bold text-base
                hover:bg-white/10 transition-all duration-200
                whitespace-nowrap cursor-pointer
              "
            >
              <i className="ri-shuffle-line"></i>
              번호 만들러 가기
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <div className="md:hidden h-16"></div>
      <MobileBottomNav />
    </div>
  );
}